import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { track } from '@/lib/analytics';
import {
  buildOpenTarget,
  readPromptState,
  resolveEligibility,
  shouldShowBanner,
  shouldShowDialog,
  writePromptState,
  type DownloadPromptState,
} from '@/lib/app-download-prompt';

export type PromptSource = 'modal' | 'banner' | 'menu';

/**
 * Drives the mobile-web "download the app" nudges (modal, footer banner, menu item).
 */
export function useAppDownloadPrompt() {
  const isMobile = useIsMobile();
  const { settings } = useSystemSettings();
  const [state, setState] = useState<DownloadPromptState>(() => readPromptState());

  const { eligible, platform, storeUrl } = resolveEligibility({
    isNativeApp: Capacitor.isNativePlatform(),
    isMobileViewport: !!isMobile,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    androidUrl: settings.app_store_url_android,
    iosUrl: settings.app_store_url_ios,
  });

  const now = Date.now();
  const canShowDialog = eligible && shouldShowDialog(state, now);
  const canShowBanner = eligible && shouldShowBanner(state, now);

  const [dialogOpen, setDialogOpen] = useState(false);

  // Open the modal once per eligible session.
  useEffect(() => {
    if (canShowDialog) setDialogOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShowDialog]);

  const persist = useCallback((patch: DownloadPromptState) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      writePromptState(next);
      return next;
    });
  }, []);

  const trackPrompt = useCallback(
    (event: 'app_download_prompt_shown' | 'app_download_prompt_dismissed' | 'app_download_prompt_clicked', source: PromptSource) => {
      track(event, { source, platform });
    },
    [platform],
  );

  const openStore = useCallback(
    (source: PromptSource) => {
      if (!storeUrl) return;
      trackPrompt('app_download_prompt_clicked', source);
      persist({ convertedAt: Date.now() });
      setDialogOpen(false);
      const target = buildOpenTarget(platform, storeUrl);
      window.location.href = target;
    },
    [storeUrl, platform, persist, trackPrompt],
  );

  const dismissDialog = useCallback(() => {
    trackPrompt('app_download_prompt_dismissed', 'modal');
    persist({ dismissedAt: Date.now() });
    setDialogOpen(false);
  }, [persist, trackPrompt]);

  const closeBanner = useCallback(() => {
    trackPrompt('app_download_prompt_dismissed', 'banner');
    persist({ bannerClosedAt: Date.now() });
  }, [persist, trackPrompt]);

  return {
    /** True when a store link exists for this mobile browser. */
    eligible,
    platform,
    storeUrl,
    dialogOpen: dialogOpen && canShowDialog,
    showBanner: canShowBanner,
    openStore,
    dismissDialog,
    closeBanner,
    trackShown: (source: PromptSource) => trackPrompt('app_download_prompt_shown', source),
  };
}
